// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Lustre — the opponent. A deterministic pure function of (state, seat, tier, salt), which is what
// lets it be three different things at once: the solo opponent, the fill-in for a seat that has gone
// quiet in a live room (byte-identical on every peer, so the log dedupes it), and the measuring
// instrument the balance sim runs a few hundred games through.
//
// THE HARD PART IS POT — how much Lustre I could still bank if I stopped building now. Cutting is
// priced off a dial that RIVALS also push, so a bench is worth what you can convert before the price
// outruns you, not what it cost. A bot that scores its bench by size hoards until the Close and
// finishes with a magnificent workshop and no points; that is exactly the failure the design is
// built to punish, so the bot has to be able to see it.
//
// POT is EXACT CONDITIONAL ON ρ — the enumeration over value-class counts is exact, but every future
// price depends on a predicted rate at which the rest of the table banks. It is a good guess, not an
// oracle, and it is labelled as one because a balance number certified by a bot that believes its
// own forecast is a balance number about the forecast.
//
// All arithmetic is fixed-point ×1000 so the argmax tie-break is exact on every machine.

import {
  TIERS, HAND_CAP,
} from './cards';
import {
  aVerb, actingSeat, applyPly, bandOf, bite, cloneState, cutPrice, handSize, isRough,
  legalActions, pips, roughCount, stepPrice, V_CLAIM, V_CUT, V_GATHER, V_PATRON, V_SHELVE,
  type Player, type State,
} from './rules';

export type TierName = 'apprentice' | 'cutter' | 'lapidary';

export interface BotTier {
  id: TierName;
  name: string;
  /** Ignore tick cost entirely and score the bench by size — the beatable arm. */
  naive: boolean;
  /** Chance of a random legal action instead of the best one, in thousandths. */
  noise: number;
  /** Weight on wrecking a rival's Patron progress. */
  denial: number;
  /** Price the next cut against the band the dial will have reached, not today's. */
  lookahead: boolean;
}

export const TIERS_BY_ID: Record<TierName, BotTier> = {
  apprentice: { id: 'apprentice', name: 'Apprentice', naive: true, noise: 250, denial: 0, lookahead: false },
  cutter: { id: 'cutter', name: 'Cutter', naive: false, noise: 0, denial: 0, lookahead: false },
  lapidary: { id: 'lapidary', name: 'Lapidary', naive: false, noise: 0, denial: 400, lookahead: true },
};

export const TIER_IDS: TierName[] = ['apprentice', 'cutter', 'lapidary'];

export function tierOf(id: unknown): BotTier {
  return typeof id === 'string' && Object.hasOwn(TIERS_BY_ID, id)
    ? TIERS_BY_ID[id as TierName]
    : TIERS_BY_ID.cutter;
}

/**
 * Control arms, used ONLY by the sim. Each disables exactly one belief, so a graft that does nothing
 * shows up as a control that does not lose.
 */
export interface Handicap {
  /** Assume rivals never bank anything: the dial only moves when I cut. */
  rivalBlind?: boolean;
  /** Price every future cut at today's band, forever. */
  bandBlind?: boolean;
  /** Forbidden from cutting until this many ticks remain. */
  noCutUntil?: number;
}

const K = 1000;
const W_LUSTRE = 1000;
const W_POT = 900;
const W_BITE = 180;
const W_PIPS = 160;
const W_GRIT = 60;
const W_OPAL = 140;
const W_PATRON_NEAR = 500;
const W_ROUGH_OVER = 200;
const W_HAND_OVER = 150;

/** Lustre a seat is expected to bank over a whole game. Only ρ's prior depends on it. */
export const EXPECTED_LUSTRE = 12;

/**
 * ρ — how fast the table banks Lustre, in Lustre per tick. Starts as a prior derived from the mode's
 * length and player count, then blends toward what has actually been observed as evidence arrives.
 */
export function rho(s: State, h: Handicap = {}): number {
  if (h.rivalBlind) return 0;
  const prior = (EXPECTED_LUSTRE * s.n) / s.close;
  const bv = bandOf(s);
  if (s.dial < bv) return prior;
  let maxTick = 1;
  for (const p of s.players) maxTick = Math.max(maxTick, p.pawn);
  const observed = s.dial / maxTick;
  const w = bv / (bv + s.dial);
  return observed * (1 - w) + prior * w;
}

/**
 * The best total cut value this seat could still bank, given its remaining ticks and a forecast of
 * where the dial will be when it spends them.
 *
 * Exact over which SET to cut — ordering inside a chosen set is irrelevant because the price depends
 * only on the cumulative dial, and cutting the biggest first is optimal within a set. So enumerate
 * by how many of each value class to take: at most 9 × 5 × 4 combinations.
 */
export function pot(s: State, seat: number, h: Handicap = {}): number {
  const p = s.players[seat];
  if (s.mode.patronIsVerb) return 0;
  const counts = [0, 0, 0];
  for (const sp of p.bench) if (isRough(sp)) counts[sp.tier]++;
  const budget0 = s.close - p.pawn;
  if (budget0 <= 0) return 0;
  const r = rho(s, h);
  const bv = bandOf(s);
  const tail = s.mode.tailStep;
  const todayBand = Math.floor(s.dial / bv);

  let best = 0;
  for (let k = 0; k <= counts[2]; k++) {
    for (let j = 0; j <= counts[1]; j++) {
      for (let i = 0; i <= counts[0]; i++) {
        if (i + j + k === 0) continue;
        let dial = s.dial;
        let budget = budget0;
        let spent = 0;
        let value = 0;
        let feasible = true;
        // Descending value is optimal within a chosen set: the price ladder is monotone, so paying
        // the cheap bands for the big spars is never worse.
        const plan = [
          [2, k],
          [1, j],
          [0, i],
        ] as const;
        for (const [tier, count] of plan) {
          for (let c = 0; c < count && feasible; c++) {
            const band = h.bandBlind ? todayBand : Math.floor((dial + r * spent) / bv);
            const price = stepPrice(band, tail);
            if (price > budget) {
              feasible = false;
              break;
            }
            budget -= price;
            spent += price;
            dial += TIERS[tier].cut;
            value += TIERS[tier].cut;
          }
          if (!feasible) break;
        }
        if (value > best) best = value;
      }
    }
  }
  return best;
}

/** How close this seat is to any Patron still on the table, weighted by what it pays. */
function patronNear(s: State, seat: number): number {
  const p = s.players[seat];
  if (p.patrons.length >= s.mode.patronCap) return 0;
  const pip = pips(p);
  let total = 0;
  for (const id of s.patronsUp) {
    if (id < 0) continue;
    const def = s.patronDefs[id];
    let missing = 0;
    for (let hue = 0; hue < 4; hue++) missing += Math.max(0, def.need[hue] - pip[hue]);
    const closeness = Math.max(0, 1 - missing / 3);
    if (closeness <= 0) continue;
    // Halve anything a rival already qualifies for — it is very likely gone before I get there.
    let contested = false;
    for (let other = 0; other < s.n && !contested; other++) {
      if (other === seat) continue;
      if (s.players[other].patrons.length >= s.mode.patronCap) continue;
      const op = pips(s.players[other]);
      contested = def.need.every((need, hue) => op[hue] >= need);
    }
    total += def.value * closeness * (contested ? 0.5 : 1);
  }
  return total;
}

/** The evaluation, in fixed point ×1000. */
export function evaluate(s: State, seat: number, tier: BotTier, h: Handicap = {}): number {
  const p: Player = s.players[seat];
  let patronValue = 0;
  for (const id of p.patrons) patronValue += s.patronDefs[id].value;
  const potential = tier.naive ? 2.5 * roughCount(p) : pot(s, seat, h);
  const pip = pips(p);
  const pipTotal = pip[0] + pip[1] + pip[2] + pip[3];
  const hand = handSize(p);

  let e = 0;
  e += W_LUSTRE * (p.lustre + patronValue);
  e += W_POT * potential;
  e += W_BITE * bite(p);
  e += W_PIPS * pipTotal;
  e += W_GRIT * (hand - p.opals);
  e += W_OPAL * p.opals;
  e += Math.round(W_PATRON_NEAR * patronNear(s, seat));
  e -= W_ROUGH_OVER * Math.max(0, roughCount(p) - (s.mode.roughCap - 1));
  e -= W_HAND_OVER * Math.max(0, hand - (HAND_CAP - 1));
  return Math.round(e);
}

/** Total Patron progress this action destroys for rivals — LAPIDARY's reason to shelve a card. */
function denialGain(before: State, after: State, seat: number): number {
  let gain = 0;
  for (let other = 0; other < before.n; other++) {
    if (other === seat) continue;
    gain += patronNear(before, other) - patronNear(after, other);
  }
  return gain;
}

/**
 * Pick a move. Deterministic: same (state, seat, tier, salt) gives the same packed int on every peer,
 * which is what makes filling in for a silent seat safe in a hostless room.
 */
export function choose(s: State, seat: number, tier: BotTier, salt = 0, h: Handicap = {}): number {
  if (s.over || actingSeat(s) !== seat) return -1;
  let actions = legalActions(s);
  if (actions.length === 0) return -1;

  if (h.noCutUntil !== undefined) {
    const ticksLeft = s.close - s.players[seat].pawn;
    if (ticksLeft > h.noCutUntil) {
      const kept = actions.filter((a) => aVerb(a) !== V_CUT);
      // Never strand the seat: if cutting was the only legal act, it still has to act.
      if (kept.length > 0) actions = kept;
    }
  }

  if (tier.noise > 0) {
    const roll = mix32(s.ply * 2654435761 + seat * 40503 + salt) % 1000;
    if (roll < tier.noise) return actions[mix32(salt + s.ply * 7919 + seat) % actions.length];
  }

  const baseE = evaluate(s, seat, tier, h);
  let best = -1;
  let bestScore = -Infinity;
  for (const a of actions) {
    const trial = cloneState(s);
    if (!applyPly(trial, a)) continue;
    let delta = evaluate(trial, seat, tier, h) - baseE;
    if (tier.denial > 0) delta += Math.round(tier.denial * denialGain(s, trial, seat));
    if (tier.lookahead && aVerb(a) === V_CUT) {
      // Cutting now buys today's band; waiting buys whatever the table has pushed it to by then.
      const later = stepPrice(
        Math.floor((s.dial + rho(s, h) * 6) / bandOf(s)),
        s.mode.tailStep,
      );
      delta += (later - cutPrice(s)) * 40;
    }
    const cost = Math.max(1, trial.players[seat].pawn - s.players[seat].pawn);
    const scored = tier.naive ? delta : Math.round((delta * K) / cost);
    // Ties break on the packed int, so two peers filling in for the same seat agree byte for byte.
    if (scored > bestScore || (scored === bestScore && a < best)) {
      bestScore = scored;
      best = a;
    }
  }
  return best;
}

function mix32(v: number): number {
  let h = v >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}

/** Verb mix, for the mechanism audit's "is any verb dead?" check. */
export const VERBS_WATCHED = [V_GATHER, V_CLAIM, V_SHELVE, V_CUT, V_PATRON];
